import type { 
  GetMarketingMaterialsCountData,
  GetMarketingMaterialsCountResponse,

  GetMarketingMaterialsData,
  GetMarketingMaterialsResponse,

  PostMarketingMaterialsData,
  PostMarketingMaterialsResponse,

  GetMarketingMaterialsByIdData,
  GetMarketingMaterialsByIdResponse,

  PutMarketingMaterialsByIdData,
  PutMarketingMaterialsByIdResponse,

  DeleteMarketingMaterialsByIdData,
  DeleteMarketingMaterialsByIdResponse
} from "@/types/openapi-ts";
import type { AxiosInstance } from "axios";

export function getMarketingMaterialsApiModule(affiliatesApi: AxiosInstance) {
  return {
    /**
     * Get marketing materials count
     *
     * @param {Object} params
     * @param {String} [params.search]
     * @param {String} [params.language_code]
     * @param {String} [params.type]
     * @param {String} [params.status]
     *
     * @return {Promise<Object>} api response
     */
    getMarketingMaterialsCount(params: GetMarketingMaterialsCountData['query']) {
      return affiliatesApi.get<GetMarketingMaterialsCountResponse>('/marketing-materials/count', { params });
    },

    /**
     * Get marketing materials
     *
     * @param {Object} params
     * @param {String} [params.sort_column]
     * @param {String} [params.sort_dir]
     * @param {String} [params.search]
     * @param {String} [params.language_code]
     * @param {String} [params.type]
     * @param {String} [params.status]
     * @param {Number} [params.limit]
     * @param {Number} [params.offset]
     *
     * @return {Promise<Object>} api response
     */
    getMarketingMaterials(params: GetMarketingMaterialsData['query']) {
      return affiliatesApi.get<GetMarketingMaterialsResponse>('/marketing-materials', { params });
    },

    /**
     * Create marketing material
     *
     * @param {Object} data
     * @param {String} data.name
     * @param {String} data.type
     * @param {String} data.status
     * @param {String} data.landing_path
     * @param {String[]} data.supported_platforms
     * @param {Object[]} data.assets
     * @param {String} data.assets[].file_id
     * @param {String[]} data.assets[].sizes
     * @param {String[]} data.assets[].language_codes
     *
     * @return {Promise<Object>} api response
     */
    createMarketingMaterial(data: PostMarketingMaterialsData['body']) {
      return affiliatesApi.post<PostMarketingMaterialsResponse>('/marketing-materials', data);
    },

    /**
     * Get marketing material by id
     *
     * @param {String} id
     *
     * @return {Promise<Object>} api response
     */
    getMarketingMaterialById(id: string) {
      return affiliatesApi.get<GetMarketingMaterialsByIdResponse>(`/marketing-materials/${id}`);
    },

    /**
     * Update marketing material
     *
     * @param {String} id
     * @param {Object} data
     * @param {String} data.name
     * @param {String} data.status
     * @param {String[]} data.supported_platforms
     * @param {Object[]} data.assets
     * @param {String} data.assets[].file_id
     * @param {String[]} data.assets[].sizes
     * @param {String[]} data.assets[].language_codes
     *
     * @return {Promise<Object>} api response
     */
    updateMarketingMaterial(id: string, data: PutMarketingMaterialsByIdData['body']) {
      return affiliatesApi.put<PutMarketingMaterialsByIdResponse>(`/marketing-materials/${id}`, data);
    },

    /**
     * Delete marketing material by id
     *
     * @param {String} id
     *
     * @return {Promise<Object>} api response
     */
    deleteMarketingMaterial(id: string) {
      return affiliatesApi.delete<DeleteMarketingMaterialsByIdResponse>(`/marketing-materials/${id}`);
    },
  }
}
